.object {
  position: absolute;
  top: 0;
  left: 0;
  transform-origin: 0 0;
  background: var(--surface);
  border: 1px solid var(--surface-edge);
  border-radius: var(--radius);
  box-shadow: var(--surface-shadow);
  overflow: hidden;
  display: flex;
  flex-direction: column;
  contain: layout paint;
}

.object[data-hovered='true']:not([data-selected='true']) {
  outline: 1px solid var(--focus-soft);
  outline-offset: 2px;
}

.object[data-selected='true'] {
  outline: 2px solid var(--focus);
  outline-offset: 2px;
}

.object[data-kind='note'] {
  background: var(--note-paper);
}

.object[data-color='sand'] {
  background: var(--note-sand);
}

.object[data-color='moss'] {
  background: var(--note-moss);
}

.object[data-color='sky'] {
  background: var(--note-sky);
}

.object[data-color='clay'] {
  background: var(--note-clay);
}

.object[data-kind='group'],
.object[data-kind='folder'] {
  overflow: visible;
  contain: layout;
}

.object[data-kind='group'] {
  background: transparent;
  border-style: dashed;
  box-shadow: none;
}

.titlebar {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 6px 10px;
  border-bottom: 1px solid var(--surface-edge);
  color: var(--ink-soft);
  font-size: 13px;
  cursor: grab;
  flex: none;
}

.titlebar strong {
  font-weight: 600;
  color: var(--ink);
}

.titlebar span {
  color: var(--ink-faint);
  font-family: var(--font-mono);
  font-size: 11px;
}

.body {
  flex: 1;
  min-height: 0;
}

.noteText {
  width: 100%;
  height: 100%;
  border: 0;
  outline: 0;
  resize: none;
  background: transparent;
  padding: 12px 14px;
  color: var(--ink);
  font: inherit;
  font-size: 14px;
  line-height: 1.5;
}

.missingApp {
  padding: 14px;
  color: var(--ink-faint);
  font-size: 13px;
}

.object[data-kind='folder'] {
  background: rgba(239, 220, 181, 0.35);
  border-style: solid;
  box-shadow: none;
}

.object[data-locked='true'] {
  outline: 1px dashed var(--ink-faint);
  outline-offset: 2px;
}

.object[data-kind='image'] {
  background: var(--ink);
}

.containerLabel {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 28px;
  padding: 0 10px;
  display: flex;
  gap: 8px;
  align-items: baseline;
  font-size: 12px;
  color: var(--ink-soft);
  white-space: nowrap;
  cursor: grab;
}

.containerLabel span {
  color: var(--ink-faint);
  font-family: var(--font-mono);
  font-size: 11px;
}

.tile {
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 4px;
  padding: 12px 14px;
  min-height: 0;
  overflow: hidden;
}

.tileIcon {
  font-size: 22px;
  color: var(--ink-faint);
  line-height: 1;
}

.tileTitle {
  font-weight: 600;
  font-size: 13px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.tileMeta {
  color: var(--ink-faint);
  font-family: var(--font-mono);
  font-size: 11px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.tileLink {
  color: var(--focus);
  font-size: 12px;
  text-decoration: none;
}

.tileLink:hover {
  text-decoration: underline;
}

.terminal {
  flex: 1;
  margin: 0;
  padding: 10px 12px;
  background: var(--terminal-bg);
  color: var(--terminal-ink);
  font-family: var(--font-mono);
  font-size: 12px;
  line-height: 1.45;
  overflow: auto;
  white-space: pre-wrap;
}

.image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
  pointer-events: none;
}

.appTile {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  border: 0;
  background: transparent;
  padding: 10px;
}

.appGlyph {
  width: 48px;
  height: 48px;
  display: grid;
  place-items: center;
  border-radius: 12px;
  background: var(--note-sky);
  color: var(--ink);
  font-size: 22px;
  font-weight: 600;
}

.window {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
}

.object[data-kind='window'] .window[data-active='true'] {
  box-shadow: inset 0 0 0 1px var(--focus-soft);
}

.window[data-active='true'] .titlebar {
  background: var(--surface);
  color: var(--ink);
}

.windowIcon {
  width: 18px;
  text-align: center;
  color: var(--ink-faint);
}

.windowControls {
  margin-left: auto;
  display: flex;
  gap: 2px;
  font-family: inherit;
}

.object[data-kind='window'] .body {
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

@keyframes highlight-pulse {
  0% {
    outline-color: var(--focus);
    outline-offset: 14px;
    outline-width: 3px;
  }
  100% {
    outline-color: transparent;
    outline-offset: 2px;
    outline-width: 3px;
  }
}

.object[data-highlighted='true'] {
  outline: 3px solid var(--focus);
  animation: highlight-pulse 900ms ease-out 2;
}

.containerLabel[data-collapsed='true'] {
  background: var(--surface);
  border-bottom: 0;
}

.object[data-kind='group'][data-collapsed='true'],
.object[data-kind='folder'][data-collapsed='true'] {
  background: var(--surface);
  border-style: solid;
  box-shadow: var(--surface-shadow);
  overflow: hidden;
}

/* Compact controls inside window title bars and container headers. */
.windowButton {
  width: 22px;
  height: 22px;
  min-width: 22px !important;
  min-height: 22px !important;
  padding: 0 !important;
}
